import React, { useMemo } from 'react';
import styled from 'styled-components/macro';

import useMedia from 'hooks/useMedia';
import MobileTopicFiltersContainer from 'components/TopicFilters/MobileTopicFiltersContainer';
import {
  ClearButton,
  Button,
  ClearButtonCloseIcon,
} from 'components/TopicFilters/TopicFilters.styles';
import DesktopFiltersContainer from 'components/TopicFilters/DesktopFiltersContainer';
import CountryDropdown from 'components/base/CountryDropdown';
import { ICountry, IChartDataWithDetails } from '../types';
import { MenuButton } from 'components/base/Menu/index';
import ChartTopicFilterDropdown from './ChartTopicFilterDropdown';
import { DropdownLabel } from 'components/base/TopicFiltersDropdown/shared/styles';

const TopicFilterContainer = styled('div')<any>`
  font-size: 1em;
  font-weight: 600;
  margin-right: ${({ theme }): string => theme.spacing.small};
  border-top: 2px solid #fbfbfb;
  @media screen and (min-width: 40em) {
    border: none;
    display: flex;
    @media screen and (max-width: 50em) {
      padding-right: 0.5em;
      padding-left: 0.5em;
    }
  }
  span {
    text-transform: capitalize;
  }
  ${DropdownLabel} {
    text-transform: uppercase;
  }
  ${MenuButton} > span {
    &:first-of-type {
      padding: 0.25rem 0.75rem;
    }
    &:nth-of-type(2) {
      padding: 0;
    }
  }
  @media screen and (min-width: 40em) {
    ${MenuButton} {
      div {
        padding-top: 0.2rem;
        padding-bottom: 0.2rem;
        font-size: 13px;
      }
    }
  }
`;

const TopicFiltersWrapper = styled('div')`
  @media screen and (min-width: 40em) {
    & > div {
      width: initial;
    }
  }
  @media screen and (min-width: 50em) {
    margin: 0;
    > div {
      width: auto;
    }
  }
  @media screen and (min-width: 60em) {
    margin-left: 2em;
    margin-right: 2em;
  }
  @media screen and (min-width: 80em) {
    margin-left: 8em;
    margin-right: 8em;
  }
  ${ClearButton} {
    padding: 0.55rem 1rem;
    @media screen and (min-width: 40em) and (max-width: 50em) {
      padding-left: 1em;
      padding-right: 1em;
    }
  }
`;

const getChartFrequencies = (charts: IChartDataWithDetails[]): string[] => [
  ...new Set(
    charts.reduce(
      (prev: string[], current: IChartDataWithDetails) => [
        ...prev,
        current.updateFrequency,
      ],
      []
    )
  ),
];

const getChartFrequenciesToDisplay = (
  frequencies: string[]
): { label: string; value: string }[] =>
  frequencies.map((item: string) => ({ label: item, value: item }));

interface IProps {
  chartsList: IChartDataWithDetails[];
  countriesList: ICountry[];
  filteredChartsList: IChartDataWithDetails[] | undefined;
  selectedChart?: IChartDataWithDetails;
  selectedChartDate: any;
  availableDates: any[];
  selectedCountry?: ICountry;
  setSelectedChart: Function;
  setSelectedCountry: Function;
  setSelectedFrequency: Function;
  setSelectedDate: Function;
  resetDefaultChartCountries: (charts: IChartDataWithDetails[]) => void;
  isLoadedCustomers: boolean;
}

const ChartsTopicFilters: React.FC<IProps> = (props) => {
  const {
    chartsList,
    countriesList,
    filteredChartsList,
    selectedChart,
    selectedChartDate,
    availableDates,
    selectedCountry,
    setSelectedChart,
    setSelectedCountry,
    setSelectedFrequency,
    setSelectedDate,
    resetDefaultChartCountries,
    isLoadedCustomers,
  } = props;

  const isSmallScreen = useMedia('(max-width: 40em)');
  const TopicFiltersContainer = isSmallScreen
    ? MobileTopicFiltersContainer
    : DesktopFiltersContainer;

  const filterCount = [
    selectedChart,
    selectedCountry,
    selectedChart?.updateFrequency,
    selectedChartDate,
  ].filter((item) => !!item).length;

  const frequencies = useMemo(
    () => chartsList && getChartFrequencies(chartsList),
    [chartsList]
  );
  const frequenciesToDisplay =
    frequencies && getChartFrequenciesToDisplay(frequencies);

  const mobileFilterTitle = filterCount === 0 ? 'Filters' : 'Filters applied';

  return (
    <TopicFiltersWrapper>
      <TopicFiltersContainer
        filterCount={filterCount}
        title={mobileFilterTitle}
        topic="charts"
      >
        <TopicFilterContainer>
          <ChartTopicFilterDropdown
            onChange={(item: IChartDataWithDetails): void =>
              setSelectedChart(item)
            }
            items={filteredChartsList || []}
            selectedItem={selectedChart}
            defaultLabel={
              isLoadedCustomers && filteredChartsList?.length === 0
                ? 'Not Available'
                : 'Chart Name'
            }
            filterLabel="Chart Name"
          />
        </TopicFilterContainer>

        <TopicFilterContainer>
          <CountryDropdown
            onChange={(item: ICountry): void => setSelectedCountry(item)}
            items={countriesList}
            selectedItem={selectedCountry}
            defaultLabel="Global"
            isDisplayedWithFlag={false}
            clearFilter={(): void =>
              setSelectedCountry(countriesList.find((item) => item.id === 'WW'))
            }
            isTopicFilter
            checkIfOnlyOneValue
          >
            <Button
              borderColor={'LightGrey'}
              borderWidth={'1px'}
              borderStyle={'solid'}
              py={4}
              onClick={(): void =>
                setSelectedCountry(
                  countriesList.find((item) => item.id === 'WW')
                )
              }
            >
              Clear this filter
            </Button>
          </CountryDropdown>
        </TopicFilterContainer>

        <TopicFilterContainer>
          <ChartTopicFilterDropdown
            onChange={(item: { label: string; value: string }): void =>
              setSelectedFrequency(item.value)
            }
            items={frequenciesToDisplay || []}
            selectedItem={
              selectedChart
                ? {
                    ...selectedChart,
                    label: selectedChart?.updateFrequency,
                  }
                : undefined
            }
            defaultLabel={
              isLoadedCustomers && frequenciesToDisplay?.length === 0
                ? 'Not Available'
                : 'Frequency'
            }
            filterLabel="Frequency"
          />
        </TopicFilterContainer>

        <TopicFilterContainer>
          <ChartTopicFilterDropdown
            items={availableDates}
            onChange={(item: { label: string; value: string }): void =>
              setSelectedDate(item.value)
            }
            selectedItem={selectedChartDate}
            defaultLabel="Date"
            filterLabel="Date"
          />
        </TopicFilterContainer>

        <ClearButton
          isActive={filterCount > 0}
          onClick={(): void => resetDefaultChartCountries(chartsList)}
          isSmallScreen={isSmallScreen}
        >
          <ClearButtonCloseIcon />
          {isSmallScreen ? 'Reset All Filters' : 'Reset all'}
        </ClearButton>
      </TopicFiltersContainer>
    </TopicFiltersWrapper>
  );
};

export default ChartsTopicFilters;
